<script setup>
import {
  useTemplateRef,
  ref,
  computed,
  watch,
  onMounted,
  inject,
  shallowRef,
  toValue,
} from 'vue'
import { useRouter } from 'vue-router'
import { format } from 'date-fns'
import { storeToRefs } from 'pinia'
import { useDebounceFn } from '@vueuse/core'
import { commons } from '@/lib/transformer'
import Mod from '@/components/mod.vue'
import { useUserStore } from '@/stores/user'
import View from './view.vue'
import {
  extensions,
  extensionsImages,
  getEditorConfig,
  getExtension,
  isPdfUpload,
} from './view'
import { defaultConfig, useApi, ApiError } from '../../composables/api'
import config from '@/config'

/**
 * Режим карточки (Form / зона SoH). Вне карточки — true: Finder, списки, /docview.
 * Карандаш в просмотрщике только при редактировании карточки.
 */
const formEditable = inject('form:editable', () => shallowRef(true), true)

/** Шаг и диапазон в целых процентах — без float-погрешности (0.1+0.2…). */
const SCALE_STEP_PERCENT = 25
/** Требование: масштаб 10%–500%. */
const SCALE_MIN_PERCENT = 10
const SCALE_MAX_PERCENT = 500

/**
 * Ограничивает масштаб допустимым диапазоном (целые %).
 *
 * @param {number} percent масштаб в процентах (100 = 100%).
 * @returns {number} целый процент в [SCALE_MIN_PERCENT, SCALE_MAX_PERCENT].
 */
function clampPercent(percent) {
  const n = Math.round(Number(percent))
  if (!Number.isFinite(n)) {
    return 100
  }
  if (n < SCALE_MIN_PERCENT) {
    return SCALE_MIN_PERCENT
  }
  if (n > SCALE_MAX_PERCENT) {
    return SCALE_MAX_PERCENT
  }
  return n
}

/**
 * Задаёт масштаб по коэффициенту (1 = 100%), со округлением до целых %.
 *
 * @param {number} factor коэффициент масштаба.
 */
function setScaleFromFactor(factor) {
  scalePercent.value = clampPercent(Number(factor) * 100)
}

/** Сброс масштаба на 100%. */
function resetScale() {
  scalePercent.value = 100
}

defineOptions({
  inheritAttrs: true,
})
const props = defineProps({
  upload: Object,
  versioning: Boolean,
  editable: {
    type: Boolean,
    default: true,
  },
  /** Текст вместо карандаша: почему форму нельзя редактировать (например, загружено ПП). */
  editBlockedHint: {
    type: String,
    default: '',
  },
  /**
   * false на маршруте /docview: свой pushState у модалки плюс router.back()
   * при закрытии дают второй шаг истории, и vue-router возвращает просмотрщик.
   */
  useHistory: {
    type: Boolean,
    default: true,
  },
  /**
   * Из R7 «Вернуться в просмотрщик» — на страницу, открывшую просмотрщик, а не
   * на отдельную /docview. Для страниц, которые сами переоткрывают файл по
   * своему URL (Finder: ?link=<id>).
   */
  returnToCaller: {
    type: Boolean,
    default: false,
  },
  /**
   * Поверх уже открытой модалки (история): без второго backdrop и без
   * history.pushState, чтобы Back не закрывал историю вместе с просмотрщиком.
   */
  shiftLeft: Boolean,
  /** full — на весь экран; xl/lg — компактнее, как передаёт история. */
  modalSize: {
    type: String,
    default: '',
  },
  modalClass: {
    type: String,
    default: '',
  },
  /**
   * PDF не из docparser (например, проект досье в S3): `{ link, download }`.
   * `link` — blob/URL PDF (пока null — спиннер), `download()` — скачивание
   * вместо docparser /download. Без docs:info, версий, редактора и пароля;
   * `upload` даёт только имя, дату и автора для боковой панели.
   */
  direct: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['hidden'])

const opensOverParent = computed(() => props.shiftLeft)
const viewerSize = computed(() => props.modalSize || 'full')
const viewerUseHistory = computed(
  () => props.useHistory !== false && !opensOverParent.value
)
const viewerBackdrop = computed(() => !opensOverParent.value)
const viewerModalClass = computed(() => {
  const parts = []
  if (opensOverParent.value) {
    parts.push('docviewer-from-history')
  }
  if (props.modalClass) {
    parts.push(props.modalClass)
  }
  if (viewerSize.value !== 'full') {
    parts.push('docviewer-modal-compact')
  }
  return parts.join(' ')
})

const router = useRouter()
const view = useTemplateRef('view')
const passwordRef = useTemplateRef('password')
const unlockRef = useTemplateRef('unlock')
const userStore = useUserStore()

const displayVersion = ref({ version: 0 })
const uploadr = ref(props.upload)
const pageTotal = ref(1)
const pageCurrent = ref(1)
/** Источник истины — целые %; коэффициент для View/PDF = percent/100. */
const scalePercent = ref(100)
const scale = computed(() => scalePercent.value / 100)
const loading = ref(true)
/** Только операции set/reset пароля — не ждать загрузку PDF (~десятки секунд). */
const passwordBusy = ref(false)
const printing = ref(false)
const passwordShow = ref(false)
const passwordValue = ref('')
const oldPassword = ref('')
const unlockShow = ref(false)
const unlockPassword = ref('')
const unlockError = ref('')
const passwordNotice = ref('')
const passwordNoticeRef = useTemplateRef('passwordNotice')

const fileOk = computed(() => extensions.includes(getExtension(uploadr.value)))
const isImage = computed(() => extensionsImages.includes(getExtension(uploadr.value)))
/**
 * В SSO/LTMS поле ``email`` — это PRID; настоящий адрес — ``email_notifications``.
 * Старые payload'ы без email_notifications: показываем email только если похож на почту.
 */
const authorEmail = computed(() => {
  const author = uploadr.value?.author
  if (!author || typeof author !== 'object') {
    return ''
  }
  const notify = String(author.email_notifications || '').trim()
  if (notify) {
    return notify
  }
  const raw = String(author.email || '').trim()
  return raw.includes('@') ? raw : ''
})
/**
 * Общие условия карточки для карандаша/ключа: editable + режим редактирования.
 * form:editable скрывает только при явном false (view-режим Form).
 * undefined/null — как вне карточки (Досье, история, /docview): разрешить.
 */
function canProtectOrEditInCard() {
  if (!props.editable) {
    return false
  }
  const fe = toValue(formEditable)
  if (fe === false) {
    return false
  }
  return true
}
/**
 * Карандаш R7 — только office (word/cell/slide). PDF/картинки — без редактора.
 */
const canOpenEditor = computed(() => {
  if (!canProtectOrEditInCard()) {
    return false
  }
  if (!uploadr.value?.dockey) {
    return false
  }
  return Boolean(getEditorConfig(uploadr.value))
})
/**
 * Ключ: office (как карандаш) + PDF — защита скачивания (*.restricted.pdf / Acrobat).
 * Картинки без защиты.
 * PDF: isPdfUpload (basename/mime/`_pdf`) — старые finder-файлы без точки в path.
 * Досье: list.vue provide form:editable=true (вне Form inject мог скрывать ключ).
 */
const canSetPassword = computed(() => {
  if (!canProtectOrEditInCard()) {
    return false
  }
  if (!uploadr.value?.secure_filename) {
    return false
  }
  if (isPdfUpload(uploadr.value)) {
    return true
  }
  // Office: тот же контур, что карандаш (нужен dockey для R7-цепочки).
  return Boolean(uploadr.value?.dockey && getEditorConfig(uploadr.value))
})

const allowView = userStore.can('download_files')
/** Прямой файл: права на скачивание и печать решает тот, кто его выдал. */
const isDirect = computed(() => Boolean(props.direct))
const allowDerestrict = userStore.can('docs_derestrict')
const { user } = storeToRefs(userStore)

const { model, refresh } = useApi({
  url: computed(() => {
    const file =
      displayVersion.value?.secure_filename ||
      uploadr.value?.secure_filename ||
      props.upload?.secure_filename
    return file ? `upload/${file}` : ''
  }),
  immediate: false,
  before: () =>
    Boolean(
      displayVersion.value?.secure_filename ||
        uploadr.value?.secure_filename ||
        props.upload?.secure_filename
    ),
})
const { model: info, refresh: fetchInfoRaw } = useApi({
  url: 'docs:info',
  query: computed(() => {
    const service =
      displayVersion.value?.service ||
      uploadr.value?.service ||
      defaultConfig.service
    // path первым: при общем dockey у разных НД key=dockey отдаёт чужой файл
    const path =
      displayVersion.value?.secure_filename || uploadr.value?.secure_filename
    if (path) {
      return { service, path }
    }
    const docId = uploadr.value?.iddoc || uploadr.value?.id
    if (docId && !/^\d+$/.test(String(docId))) {
      return { service, id: String(docId) }
    }
    if (uploadr.value?.dockey) {
      return { service, key: String(uploadr.value.dockey) }
    }
    return { service }
  }),
  // После syncDisplayVersion в onMounted — иначе immediate уходит без path на key=dockey
  immediate: false,
  // Не затирать restricted:false до ответа — иначе «Сбросить» пропадает при открытии.
  data: {},
})

/**
 * Защита: docs:info — источник истины, если поле уже пришло.
 * Иначе props/uploadr (finder отдаёт restricted сразу).
 * Нельзя считать `info={}` / отсутствие ключа как «не защищён».
 */
const isRestricted = computed(() => {
  const fromInfo = info.value
  if (
    fromInfo &&
    typeof fromInfo === 'object' &&
    Object.prototype.hasOwnProperty.call(fromInfo, 'restricted')
  ) {
    return Boolean(fromInfo.restricted)
  }
  return Boolean(uploadr.value?.restricted ?? props.upload?.restricted)
})

/** Плашка в шапке: своя причина или ограничение паролем. */
const editHint = computed(() => {
  const custom = String(props.editBlockedHint || '').trim()
  if (custom) {
    return custom
  }
  if (isRestricted.value) {
    return 'Редактирование ограничено паролем'
  }
  return ''
})

/** Схлопнуть параллельные docs:info (mount + watch displayVersion/upload). */
let infoFetchInFlight = null
function fetchInfo() {
  if (infoFetchInFlight) {
    return infoFetchInFlight
  }
  infoFetchInFlight = Promise.resolve(fetchInfoRaw())
    .catch(err => {
      throw err
    })
    .finally(() => {
      infoFetchInFlight = null
    })
  return infoFetchInFlight
}
const { refresh: refreshPassword } = useApi({
  url: 'docs:set-password',
  method: 'post',
})
const { model: unlockResult, refresh: checkPassword } = useApi({
  url: 'docs:check-password',
  method: 'post',
  immediate: false,
  data: {},
})

function mergeUploadVersions(existing, fresh) {
  if (!fresh?.secure_filename) {
    return renumberVersionEntries(existing || [])
  }
  return mergeVersionLists(existing, [
    {
      version: fresh.version,
      secure_filename: fresh.secure_filename,
      deleted: Boolean(fresh.deleted),
      uploaded_at: fresh.uploaded_at,
      id: fresh.id,
    },
  ])
}

/** Сквозные 1..N без дублей «Версия N» (как Архив / renumber_version_entries). */
function renumberVersionEntries(list) {
  const byFile = new Map()
  for (const item of list || []) {
    if (!item?.secure_filename) {
      continue
    }
    const prev = byFile.get(item.secure_filename)
    byFile.set(item.secure_filename, {
      secure_filename: item.secure_filename,
      deleted: Boolean(item.deleted),
      uploaded_at: item.uploaded_at || prev?.uploaded_at || null,
      id: item.id || prev?.id || null,
      version: item.version ?? prev?.version,
    })
  }
  const items = [...byFile.values()].sort((a, b) => {
    const ta = String(a.uploaded_at || '')
    const tb = String(b.uploaded_at || '')
    if (ta && tb && ta !== tb) {
      return ta < tb ? -1 : 1
    }
    const va = Number(a.version || 0)
    const vb = Number(b.version || 0)
    if (va !== vb) {
      return va - vb
    }
    return String(a.secure_filename).localeCompare(String(b.secure_filename))
  })
  return items.map((item, index) => ({
    version: index + 1,
    secure_filename: item.secure_filename,
    deleted: Boolean(item.deleted),
  }))
}

function mergeVersionLists(a, b) {
  return renumberVersionEntries([...(a || []), ...(b || [])])
}

/**
 * После R7 save новый path ещё нет в LTMS-архиве — без этого dropdown
 * показывает чужую «Версию N», хотя preview уже нового файла.
 */
function ensureCurrentInVersions(versions, current) {
  const path = current?.secure_filename
  if (!path) {
    return Array.isArray(versions) ? versions : []
  }
  const list = Array.isArray(versions) ? versions : []
  if (list.some(v => v.secure_filename === path)) {
    return list
  }
  return renumberVersionEntries([
    ...list,
    {
      version: list.reduce((max, v) => Math.max(max, Number(v.version) || 0), 0) + 1,
      secure_filename: path,
      deleted: Boolean(current.deleted),
      uploaded_at: current.uploaded_at || new Date().toISOString(),
      id: current.id || current.iddoc || null,
    },
  ])
}

/** Число уникальных файлов в списке версий. */
function versionFileCount(list) {
  if (!Array.isArray(list) || !list.length) {
    return 0
  }
  return new Set(list.map(v => v?.secure_filename).filter(Boolean)).size
}

/**
 * Выбрать более полный список (Архив растёт после R7/замен) и склеить по path.
 * Не брать короткий dockey-список вместо полного архива.
 */
function preferFullerVersions(primary, secondary) {
  const a = Array.isArray(primary) ? primary : []
  const b = Array.isArray(secondary) ? secondary : []
  if (!a.length) {
    return renumberVersionEntries(b)
  }
  if (!b.length) {
    return renumberVersionEntries(a)
  }
  const ca = versionFileCount(a)
  const cb = versionFileCount(b)
  if (cb > ca) {
    return renumberVersionEntries([...a, ...b])
  }
  if (ca > cb) {
    return renumberVersionEntries([...b, ...a])
  }
  return renumberVersionEntries([...a, ...b])
}

/**
 * Файл — актуальная (последняя не удалённая) версия из dropdown.
 * Без списка версий — false: не знаем, значит не считаем актуальной.
 *
 * @param {string} filename secure_filename.
 * @returns {boolean}
 */
function isLatestVersionFile(filename) {
  const list = Array.isArray(uploadr.value?.versions) ? uploadr.value.versions : []
  const live = list.filter(v => v?.secure_filename && !v.deleted)
  const pool = live.length ? live : list
  return Boolean(filename) && pool[pool.length - 1]?.secure_filename === filename
}

/** Элемент из текущего versions[] — иначе <select :value="object"> пустой (другая ссылка). */
function pickVersionEntry(versions, preferred = {}) {
  const list = versions || []
  if (!list.length) {
    return {
      version: 0,
      secure_filename: preferred.secure_filename || undefined,
    }
  }
  if (preferred.secure_filename) {
    const byFile = list.find(v => v.secure_filename === preferred.secure_filename)
    if (byFile) {
      return byFile
    }
    // Не подменять на last архива — иначе после редактора номер чужой.
    return {
      version: list.reduce((max, v) => Math.max(max, Number(v.version) || 0), 0) + 1,
      secure_filename: preferred.secure_filename,
      deleted: Boolean(preferred.deleted),
    }
  }
  if (preferred.version != null && preferred.version !== '') {
    const byVer = list.find(v => Number(v.version) === Number(preferred.version))
    if (byVer) {
      return byVer
    }
  }
  return list[list.length - 1]
}

function syncDisplayVersion(preferred) {
  if (!props.versioning || !uploadr.value?.versions?.length) {
    return
  }
  displayVersion.value = pickVersionEntry(uploadr.value.versions, preferred || {
    secure_filename:
      displayVersion.value?.secure_filename || uploadr.value.secure_filename,
    version: displayVersion.value?.version ?? uploadr.value.version,
  })
}

/** model из metadata upload (объект или JSON-строка). */
function uploadMetaModel(source) {
  let meta = source?.metadata
  if (!meta) {
    return null
  }
  if (typeof meta === 'string') {
    try {
      meta = JSON.parse(meta)
    } catch (error) {
      console.error('[soykajs] не удалось разобрать metadata загрузки', error)
      return null
    }
  }
  return meta && typeof meta === 'object' ? meta.model || null : null
}

function applyFreshUpload(fresh) {
  if (!fresh?.secure_filename) {
    return
  }
  const prev = uploadr.value || props.upload || {}
  // Карточка LTMS: versions только из Архива (props/prev). docs:info.versions
  // по dockey даёт 31 при Архиве 39 — не подмешиваем.
  // Досье (model=finder): Архива нет, цепочка R7 в /info — её и показываем.
  const infoVersions = fresh.versions
  const { versions: _ignoreInfoVersions, ...freshMeta } = fresh
  const locked = preferFullerVersions(prev.versions, props.upload?.versions)
  const isFinder =
    uploadMetaModel(freshMeta) === 'finder' ||
    uploadMetaModel(prev) === 'finder' ||
    uploadMetaModel(props.upload) === 'finder'
  const baseVersions = isFinder
    ? preferFullerVersions(locked, infoVersions)
    : locked.length
      ? locked
      : mergeUploadVersions(prev.versions, freshMeta)
  const preferFile =
    displayVersion.value?.secure_filename
    || props.upload?.secure_filename
    || freshMeta.secure_filename
    || prev.secure_filename
  const openFile =
    displayVersion.value?.secure_filename
    || props.upload?.secure_filename
    || preferFile
    || freshMeta.secure_filename
  const versions = ensureCurrentInVersions(baseVersions, {
    secure_filename: openFile,
    deleted: freshMeta.deleted ?? props.upload?.deleted ?? prev.deleted,
    uploaded_at:
      (openFile === freshMeta.secure_filename && freshMeta.uploaded_at)
      || (openFile === props.upload?.secure_filename && props.upload?.uploaded_at)
      || prev.uploaded_at,
    id: freshMeta.id || props.upload?.id || props.upload?.iddoc || prev.id,
  })
  const matched = pickVersionEntry(versions, { secure_filename: openFile })
  uploadr.value = {
    ...prev,
    ...freshMeta,
    secure_filename: openFile,
    filename:
      (openFile && openFile === props.upload?.secure_filename && props.upload?.filename)
      || (openFile && openFile === prev.secure_filename && prev.filename)
      || freshMeta.filename
      || prev.filename,
    version: matched?.version ?? prev.version ?? freshMeta.version,
    dockey: freshMeta.dockey ?? prev.dockey ?? props.upload?.dockey,
    iddoc: freshMeta.id ?? prev.iddoc,
    // Finder owner может быть без email_notifications; /info отдаёт полный User.
    author: prev.author?.username
      ? {
          ...prev.author,
          email_notifications:
            prev.author.email_notifications
            || freshMeta.author?.email_notifications
            || '',
        }
      : freshMeta.author,
    versions,
  }
  if (props.versioning && versions.length) {
    displayVersion.value = pickVersionEntry(versions, {
      secure_filename: openFile,
      version: matched?.version,
    })
  }
}

watch(
  () => props.upload,
  u => {
    if (!u?.secure_filename) {
      return
    }
    // Remount после редактора / смена версии с page.vue
    if (u.secure_filename !== uploadr.value?.secure_filename) {
      uploadr.value = { ...u }
      loading.value = true
      resetScale()
      pageCurrent.value = 1
      pageTotal.value = 1
      syncDisplayVersion({
        secure_filename: u.secure_filename,
        version: u.version,
      })
      void fetchInfo()
    }
  },
  { deep: true }
)

watch(
  info,
  fresh => {
    applyFreshUpload(fresh)
  },
  { deep: true }
)

watch(
  model,
  next => {
    if (!next?.secure_filename) {
      return
    }
    const prev = uploadr.value || {}
    const locked =
      (Array.isArray(props.upload?.versions) && props.upload.versions.length
        ? props.upload.versions
        : null)
      || (prev.versions?.length ? prev.versions : null)
    // GET /upload/:file теперь отдаёт полный Архив — не держать устаревший locked (36 < 39).
    const versions = ensureCurrentInVersions(
      preferFullerVersions(locked, next.versions),
      next
    )
    const matched = pickVersionEntry(versions || [], {
      secure_filename: next.secure_filename,
    })
    uploadr.value = {
      ...prev,
      ...next,
      versions,
      version: matched?.version ?? next.version,
    }
    syncDisplayVersion({ secure_filename: next.secure_filename })
  },
  {
    deep: true,
  }
)

watch(displayVersion, (next, prev) => {
  if (!next?.secure_filename) {
    return
  }
  // Уже смотрим этот файл — только подтянуть ссылку из актуального versions[]
  if (next.secure_filename === uploadr.value?.secure_filename) {
    const live = pickVersionEntry(uploadr.value?.versions, next)
    if (live && live !== next) {
      displayVersion.value = live
    }
    return
  }
  if (prev?.secure_filename === next.secure_filename) {
    return
  }
  resetScale()
  pageCurrent.value = 1
  pageTotal.value = 1
  loading.value = true
  // Досье: файлы только в docparser — сразу переключаем preview по path,
  // не ждём LTMS upload/ (там записи может не быть).
  uploadr.value = {
    ...(uploadr.value || {}),
    secure_filename: next.secure_filename,
    version: next.version,
    deleted: Boolean(next.deleted),
  }
  void fetchInfo()
  refresh()
})

onMounted(async () => {
  uploadr.value = { ...(props.upload || {}) }
  if (uploadr.value?.secure_filename) {
    uploadr.value.versions = ensureCurrentInVersions(
      uploadr.value.versions,
      uploadr.value
    )
    const matched = pickVersionEntry(uploadr.value.versions, {
      secure_filename: uploadr.value.secure_filename,
    })
    if (matched?.version != null) {
      uploadr.value.version = matched.version
    }
  }
  loading.value = true
  syncDisplayVersion({
    secure_filename: uploadr.value.secure_filename,
    version: uploadr.value.version,
  })
  // Без versioning syncDisplayVersion не пишет файл, и GET уходит на /upload/undefined.
  if (!displayVersion.value?.secure_filename && uploadr.value?.secure_filename) {
    displayVersion.value = {
      version: uploadr.value.version ?? 0,
      secure_filename: uploadr.value.secure_filename,
    }
  }
  if (!displayVersion.value?.secure_filename) {
    loading.value = false
    return
  }
  // Сначала полный Архив с LTMS, потом docs:info — иначе info затирает список до props (36).
  try {
    await refresh()
  } catch (error) {
    console.error(
      '[soykajs] не удалось обновить список версий загрузки',
      error
    )
  }
  try {
    await fetchInfo()
  } catch (error) {
    console.error(
      '[soykajs] не удалось получить info документа при открытии модалки',
      error
    )
  }
  syncDisplayVersion({
    secure_filename:
      props.upload?.secure_filename || uploadr.value?.secure_filename,
    version: undefined,
  })
})

function onVersionSelect(event) {
  const file = event?.target?.value
  if (!file) {
    return
  }
  const entry = pickVersionEntry(uploadr.value?.versions, {
    secure_filename: file,
  })
  if (!entry?.secure_filename) {
    return
  }
  displayVersion.value = entry
}

function onPage(current, total) {
  pageCurrent.value = current
  if (total !== undefined) {
    pageTotal.value = total
  }
}
function onLoaded(total, _scale) {
  pageTotal.value = total
  if (_scale) {
    setScaleFromFactor(_scale)
  }
  loading.value = false
}

/**
 * Увеличить масштаб: сетка 25% и нижняя точка 10% (10 → 25 → 50 → … → 500).
 */
function onScaleUp() {
  const p = scalePercent.value
  if (p < SCALE_STEP_PERCENT) {
    scalePercent.value = SCALE_STEP_PERCENT
    return
  }
  scalePercent.value = clampPercent(p + SCALE_STEP_PERCENT)
}

/**
 * Уменьшить масштаб: 500 → … → 50 → 25 → 10 (симметрично onScaleUp).
 */
function onScaleDown() {
  const p = scalePercent.value
  if (p <= SCALE_STEP_PERCENT) {
    scalePercent.value = SCALE_MIN_PERCENT
    return
  }
  scalePercent.value = clampPercent(p - SCALE_STEP_PERCENT)
}

const canScaleUp = computed(() => scalePercent.value < SCALE_MAX_PERCENT)
const canScaleDown = computed(() => scalePercent.value > SCALE_MIN_PERCENT)
const onJumpPage = useDebounceFn(event => {
  let n = Number(event.target.value)
  if (isNaN(n) || n > pageTotal.value) {
    n = pageCurrent.value
    event.target.value = n
    event.target.blur()
  }
  view.value.jumpTo(n)
}, 1000)

async function onPrint() {
  printing.value = true
  try {
    await view.value.print(150, props.upload.filename, addWatermark)
  } finally {
    printing.value = false
  }
}

/**
 *
 * @param {HTMLCanvasElement} canvas
 */
function addWatermark(canvas) {
  const ctx = canvas.getContext('2d')
  const fontsize = 20
  const height = canvas.height
  const paddingY = 5
  let maxWidth = 0
  let { fio, username } = user.value
  let data = [
    `Запрошено из: SOYKA:${String(config.VITE_SERVICE_SELF).toUpperCase()}`,
    `Инициатор: ${fio || `$user:${username}`}, AstraZeneca`,
    `Распечатано: ${format(new Date(), 'dd.MM.y HH:mm')}`,
  ]

  if (props.versioning) {
    data = [
      ...data,
      `Версия: ${displayVersion.value.version} / ${displayVersion.value.deleted ? 'неактуальная' : 'актуальная'}`,
    ]
  }
  data.reverse()

  let minY = Infinity
  let maxY = -Infinity

  ctx.save()
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.font = `${fontsize}px sans-serif`
  ctx.globalAlpha = 0.4

  for (let i = 0; i < data.length; i++) {
    const line = data[i]
    const lineWidth = ctx.measureText(line).width
    if (lineWidth > maxWidth) {
      maxWidth = lineWidth
    }

    const lineOffset = fontsize * (i + 1)

    const baselineY = height - lineOffset
    const topY = baselineY - fontsize
    const bottomY = baselineY

    if (topY < minY) {
      minY = topY
    }
    if (bottomY > maxY) {
      maxY = bottomY
    }

    ctx.fillText(line, 20, baselineY)
  }

  ctx.globalAlpha = 1
  const rectTop = minY - paddingY
  const rectHeight = maxY - minY + paddingY * 2
  ctx.strokeRect(10, rectTop, maxWidth + 20, rectHeight)
  ctx.restore()
}

function docPasswordPayload(extra = {}) {
  // path — главный ключ; id/dockey только запасной резолв на бэке.
  // service с upload (досье), иначе DELETE/POST set-password → 404 на чужом bucket.
  return {
    service:
      uploadr.value?.service ||
      displayVersion.value?.service ||
      defaultConfig.service,
    path:
      displayVersion.value?.secure_filename ||
      uploadr.value?.secure_filename,
    ...extra,
  }
}

async function setPassword() {
  const password = String(passwordValue.value || '').trim()
  if (!password) {
    return
  }
  const oldPasswordSnapshot = oldPassword.value
    ? String(oldPassword.value).trim()
    : undefined
  // Модалку закрываем сразу: POST /set-password (restrict/S3) может идти секунды.
  if (uploadr.value) {
    uploadr.value = { ...uploadr.value, restricted: true }
  }
  if (info.value && typeof info.value === 'object') {
    info.value = { ...info.value, restricted: true }
  }
  passwordRef.value?.hide()
  passwordValue.value = undefined
  oldPassword.value = undefined

  passwordBusy.value = true
  try {
    await refreshPassword({
      data: docPasswordPayload({
        password,
        old_password: oldPasswordSnapshot,
      }),
    })
    void fetchInfo()
  } catch (err) {
    if (uploadr.value) {
      uploadr.value = {
        ...uploadr.value,
        restricted: Boolean(oldPasswordSnapshot) || false,
      }
    }
    if (info.value && typeof info.value === 'object') {
      info.value = {
        ...info.value,
        restricted: Boolean(oldPasswordSnapshot) || false,
      }
    }
    const msg = String(err?.message || '')
    passwordNotice.value =
      msg && !/^error$/i.test(msg) ? msg : 'Не удалось установить пароль'
  } finally {
    passwordBusy.value = false
  }
}

function passwordHidden() {
  passwordShow.value = false
  passwordValue.value = undefined
  oldPassword.value = undefined
}

function unlockHidden() {
  unlockShow.value = false
  unlockPassword.value = ''
  unlockError.value = ''
}

/**
 * Открывает редактор R7 в текущей вкладке.
 * История: карточка → /docview → /edit, чтобы Back возвращал в просмотрщик.
 *
 * @param {string} [unlockToken] токен после успешной проверки пароля.
 * @returns {boolean} удалось ли начать переход.
 */
function openEditor(unlockToken) {
  // Явно выбранная в dropdown версия — не props.upload (часто latest с карточки)
  const filename =
    displayVersion.value?.secure_filename
    || uploadr.value?.secure_filename
    || props.upload?.secure_filename
  const infoForFile =
    info.value?.secure_filename === filename ? info.value : null
  const dockey =
    infoForFile?.dockey
    || (uploadr.value?.secure_filename === filename && uploadr.value?.dockey)
    || props.upload?.dockey
    || infoForFile?.id
    || uploadr.value?.iddoc
    || props.upload?.iddoc
  if (!filename || !dockey) {
    unlockError.value =
      'Не удалось открыть редактор: нет идентификатора документа'
    return false
  }

  // Актуальная версия — без from: редактор сам берёт latest по dockey, и запись
  // /edit в истории («вперёд» после R7 save) не закрепляется на старом файле.
  // from — только для архивной версии и когда вместо dockey пришёл id файла.
  const hasRealDockey = Boolean(
    infoForFile?.dockey
    || (uploadr.value?.secure_filename === filename && uploadr.value?.dockey)
    || props.upload?.dockey
  )
  const query = {
    ...(hasRealDockey && isLatestVersionFile(filename) ? {} : { from: filename }),
    ...(unlockToken ? { unlock: unlockToken } : {}),
  }
  const current = router.currentRoute.value
  const onViewerRoute = current.name === 'soykajs.docview'
  const sameViewerFile =
    onViewerRoute && String(current.params.filename) === String(filename)

  // Всегда кладём /docview в историю перед /edit: «Вернуться в просмотрщик»
  // и Back открывают ту же версию, а не карточку/досье под модалкой.
  // Без hide→@hidden: иначе Mod.history.back() отменяет push.
  void (async () => {
    try {
      if (onViewerRoute) {
        if (!sameViewerFile) {
          await router.replace({
            name: 'soykajs.docview',
            params: { filename },
          })
        }
      } else {
        await router.push({
          name: 'soykajs.docview',
          params: { filename },
        })
      }
      await router.push({
        name: 'soykajs.docedit',
        params: { dockey: String(dockey) },
        query,
      })
    } catch (error) {
      console.error('[soykajs] не удалось перейти в редактор', error)
    }
  })()
  return true
}

/**
 * Снять пароль/сообщение и сразу отдать hidden родителю.
 * Раньше await nextTick откладывал размонтирование — FocusTrap/backdrop
 * ещё мгновение держали блокер поверх Досье.
 */
function onMainHidden() {
  unlockShow.value = false
  passwordShow.value = false
  passwordNotice.value = ''
  emit('hidden')
}

/**
 * Актуальный restricted с docs:info до UI пароля (карточки LTMS часто без поля).
 */
async function ensurePasswordInfo() {
  const path =
    displayVersion.value?.secure_filename || uploadr.value?.secure_filename
  if (!path) {
    return
  }
  try {
    await fetchInfo()
  } catch {
    /* оставляем uploadr.restricted / props */
  }
}

/**
 * Ключ: модалку сразу; /info в фоне (не через passwordBusy — иначе при зависании
 * /info поля в модалке навсегда disabled). isRestricted обновится по ответу.
 */
function onPasswordClick() {
  passwordShow.value = true
  void ensurePasswordInfo()
}

/** Клик по карандашу: при restricted — модалка пароля, иначе сразу редактор. */
async function onEditorClick() {
  const path =
    displayVersion.value?.secure_filename
    || uploadr.value?.secure_filename
  if (path && info.value?.secure_filename !== path) {
    try {
      await fetchInfo()
    } catch (error) {
      console.error(
        '[soykajs] не удалось получить info документа перед открытием редактора',
        error
      )
      /* openEditor покажет ошибку, если dockey так и не будет */
    }
  }
  if (isRestricted.value) {
    unlockPassword.value = ''
    unlockError.value = ''
    unlockShow.value = true
    return
  }
  openEditor()
}

/**
 * Проверка пароля и переход в редактор либо сообщение «Неправильный пароль».
 */
async function submitUnlock() {
  loading.value = true
  unlockError.value = ''
  try {
    const password = String(unlockPassword.value || '').trim()
    if (!password) {
      unlockError.value = 'Введите пароль'
      return
    }
    await checkPassword({
      data: docPasswordPayload({ password }),
    })
    const token = unlockResult.value?.unlock_token
    openEditor(token)
  } catch (err) {
    const status = Number(err?.status ?? err?.code)
    const msg = String(err?.message || '')
    const isNetwork =
      err?.name === 'TypeError' ||
      /failed to fetch|networkerror|load failed/i.test(msg)
    if (isNetwork) {
      unlockError.value =
        'Нет связи с сервером документов. Проверьте docparser/nginx и обновите страницу.'
      return
    }
    const isWrong =
      (err instanceof ApiError || err?.name === 'ApiError') &&
      (status === 400 || /парол/i.test(msg))
    if (isWrong) {
      // Модалка пароля закрывается, остаётся просмотрщик и отдельное уведомление.
      unlockRef.value?.hide()
      passwordNotice.value = 'Пароль введен неверно'
      return
    }
    unlockError.value = msg || 'Не удалось проверить пароль'
  } finally {
    loading.value = false
  }
}

async function derestrict() {
  // Сразу закрыть модалку — DELETE сам по себе быстрый, но UI не должен
  // ждать общий in-flight /info или очередь воркера после set-password.
  const path =
    displayVersion.value?.secure_filename || uploadr.value?.secure_filename
  if (uploadr.value) {
    uploadr.value = { ...uploadr.value, restricted: false }
  }
  if (info.value && typeof info.value === 'object') {
    info.value = { ...info.value, restricted: false }
  }
  passwordRef.value?.hide()
  oldPassword.value = undefined
  passwordValue.value = undefined

  passwordBusy.value = true
  try {
    const payload = docPasswordPayload({})
    await refreshPassword({
      method: 'delete',
      data: payload,
      // DELETE без body на части прокси — дублируем path/service в query.
      query: {
        service: payload.service,
        path: path || payload.path,
      },
    })
    void fetchInfo()
  } catch {
    if (uploadr.value) {
      uploadr.value = { ...uploadr.value, restricted: true }
    }
    if (info.value && typeof info.value === 'object') {
      info.value = { ...info.value, restricted: true }
    }
    passwordNotice.value = 'Не удалось сбросить пароль'
  } finally {
    passwordBusy.value = false
  }
}
</script>
<template>
  <Mod
    v-if="unlockShow"
    @hidden="unlockHidden"
    ref="unlock"
    size="sm"
    :use-history="false"
    modal-class="docviewer-password-modal"
  >
    <template #header>Введите пароль</template>
    <form @submit.prevent="submitUnlock" id="docviewer-unlock-form" autocomplete="off">
      <div class="field">
        <label>Пароль для редактирования</label>
        <input
          type="password"
          name="docviewer-unlock-password"
          autocomplete="off"
          data-lpignore="true"
          data-1p-ignore="true"
          class="form-control"
          :class="{ 'is-invalid': unlockError }"
          v-model="unlockPassword"
          :disabled="loading"
          autofocus
        />
        <div v-if="unlockError" class="invalid-feedback d-block">{{ unlockError }}</div>
      </div>
    </form>
    <template #footer>
      <button
        class="btn btn-primary"
        type="button"
        :disabled="loading"
        @click="submitUnlock"
      >
        <span
          v-if="loading"
          class="spinner-border spinner-border-sm me-1"
          role="status"
          aria-hidden="true"
        />
        Ок
      </button>
      <button
        class="btn btn-secondary"
        type="button"
        @click="unlockRef.hide()"
        :disabled="loading"
      >
        Отмена
      </button>
    </template>
  </Mod>

  <Mod
    v-if="passwordNotice"
    ref="passwordNotice"
    @hidden="passwordNotice = ''"
    :use-history="false"
    size="sm"
    modal-class="docviewer-password-modal"
  >
    <template #header>Пароль</template>
    <p class="mb-0">{{ passwordNotice }}</p>
    <template #footer>
      <button class="btn btn-primary" type="button" @click="passwordNoticeRef?.hide()">
        Ок
      </button>
    </template>
  </Mod>

  <Mod
    v-if="passwordShow"
    @hidden="passwordHidden"
    ref="password"
    size="sm"
    :use-history="false"
    modal-class="docviewer-password-modal"
  >
    <template #header>{{ isRestricted ? 'Управление паролем' : 'Установить пароль' }}</template>
    <form @submit.prevent="setPassword" id="docviewer-password-form" autocomplete="off">
      <div class="field" v-if="isRestricted">
        <label>Текущий пароль</label>
        <input
          type="password"
          name="docviewer-old-password"
          autocomplete="off"
          data-lpignore="true"
          data-1p-ignore="true"
          class="form-control"
          v-model="oldPassword"
          :disabled="passwordBusy"
        />
      </div>
      <div class="field" :class="{ 'mt-2': isRestricted }">
        <label>{{ isRestricted ? 'Новый пароль' : 'Пароль' }}</label>
        <input
          type="password"
          name="docviewer-new-password"
          autocomplete="off"
          data-lpignore="true"
          data-1p-ignore="true"
          class="form-control"
          v-model="passwordValue"
          :disabled="passwordBusy"
        />
      </div>
    </form>
    <template #footer>
      <button
        class="btn btn-secondary"
        type="button"
        :disabled="passwordBusy"
        v-if="isRestricted && allowDerestrict"
        @click="derestrict"
      >
        Сбросить
      </button>
      <button
        class="btn btn-primary"
        type="button"
        :disabled="passwordBusy"
        @click="setPassword"
      >
        {{ isRestricted ? 'Сменить' : 'Сохранить' }}
      </button>
      <button
        class="btn btn-secondary"
        type="button"
        @click="passwordRef.hide()"
        :disabled="passwordBusy"
      >
        Отмена
      </button>
    </template>
  </Mod>

  <Mod
    @hidden="onMainHidden"
    :size="viewerSize"
    ref="mod"
    :use-history="viewerUseHistory"
    :backdrop="viewerBackdrop"
    :modal-class="viewerModalClass"
  >
    <template #header>
      <div class="d-flex gap-3 docview-topbar">
        <div class="hstack gap-3" v-if="pageTotal > 1 && fileOk">
          <input
            type="number"
            min="1"
            class="form-control"
            :max="pageTotal"
            :value="pageCurrent"
            :disabled="!fileOk"
            @input="onJumpPage"
          />
          <span>/</span>
          <span>{{ pageTotal }}</span>
        </div>
        <div class="hstack gap-1" v-if="fileOk">
          <button
            class="btn btn-secondary"
            type="button"
            :disabled="!canScaleUp"
            @click="onScaleUp"
          >
            <i class="bi bi-zoom-in" />
          </button>
          <span>{{ scalePercent }}%</span>
          <button
            class="btn btn-secondary"
            type="button"
            :disabled="!canScaleDown"
            @click="onScaleDown"
          >
            <i class="bi bi-zoom-out" />
          </button>
        </div>
        <button
          class="btn btn-secondary"
          type="button"
          @click="resetScale"
          v-if="scalePercent !== 100"
        >
          <i class="bi bi-arrows-fullscrenn" /> 100%
        </button>
        <button
          class="btn btn-secondary"
          type="button"
          v-if="(allowView || isDirect) && fileOk"
          :disabled="printing"
          @click="onPrint"
        >
          <i class="bi bi-printer-fill" />
        </button>
        <a
          :href="`${defaultConfig.url_docparse}download?path=${uploadr.secure_filename}&service=${config.VITE_SERVICE_SELF}&auth=${user.access_token}`"
          :download="isRestricted ? `${uploadr.filename}.restricted.pdf` : uploadr.filename"
          class="btn btn-secondary"
          v-if="allowView && !isDirect"
        >
          <i class="bi bi-cloud-download"></i>
        </a>
        <button
          v-if="isDirect"
          type="button"
          class="btn btn-secondary"
          @click="props.direct.download?.()"
        >
          <i class="bi bi-cloud-download"></i>
        </button>
        <button
          v-if="canOpenEditor"
          type="button"
          class="btn btn-secondary"
          title="Открыть в редакторе"
          @click="onEditorClick"
        >
          <i class="bi bi-pencil"></i>
        </button>
        <button
          v-if="canSetPassword"
          class="btn btn-secondary"
          type="button"
          @click="onPasswordClick"
          :disabled="passwordBusy"
          :title="isRestricted ? 'Управление паролем' : 'Установить пароль'"
        >
          <i class="bi bi-key-fill"></i>
        </button>

        <div
          v-if="editHint"
          class="docview-edit-hint"
        >
          <i
            class="bi bi-lock-fill"
            aria-hidden="true"
          ></i>
          <span>{{ editHint }}</span>
        </div>
      </div>
    </template>
    <div class="docview-ui">
      <View
        :key="uploadr.secure_filename || props.direct?.link || ''"
        :upload="uploadr"
        :pdf-direct-link="props.direct?.link || null"
        :scale="scale"
        @page="onPage"
        @loaded="onLoaded"
        ref="view"
      />
      <div class="overflow-y-auto docview-sidebar">
        <h3>Автор</h3>
        <dl>
          <template v-if="authorEmail">
            <dt>Email</dt>
            <dd>{{ authorEmail }}</dd>
          </template>
          <dt>ФИО</dt>
          <dd>{{ uploadr.author?.fio || uploadr.author?.username || '—' }}</dd>
        </dl>
        <h3>Файл</h3>
        <dl>
          <dt>Файл</dt>
          <dd>{{ uploadr.filename }}</dd>

          <dt>Загружен</dt>
          <dd>{{ commons.dateTimeLocal(uploadr.uploaded_at) }}</dd>

          <template v-if="fileOk && !isImage">
            <dt>Всего страниц</dt>
            <dd>{{ pageTotal }}</dd>
          </template>

          <template v-if="versioning && uploadr.versions?.length">
            <dt>Версия</dt>
            <dd>
              <select
                class="form-select"
                :value="displayVersion?.secure_filename || ''"
                @change="onVersionSelect"
              >
                <option
                  v-for="v of uploadr.versions"
                  :key="`${v.version}:${v.secure_filename}`"
                  :value="v.secure_filename"
                >
                  Версия {{ v.version }}
                </option>
              </select>
            </dd>
          </template>
        </dl>
      </div>
    </div>
  </Mod>
</template>

<style>
.modal.docviewer-password-modal {
  /* Вложенная модалка пароля должна быть поверх fullscreen-просмотрщика (1350). */
  z-index: 1360 !important;
}
/* Второй backdrop поверх fullscreen-просмотрщика */
body:has(.modal.docviewer-password-modal.show) .modal-backdrop.fade.show:last-of-type {
  z-index: 1290 !important;
}
</style>
